import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { hasProfileAdminRole } from '@/services/seoApprovalService';
import {
  deriveDeliveryState,
  fetchBuildState,
  fetchExpectedRevision,
  fetchPublicProfileManifest,
  requestPublicProfileBuild,
  type ProfileDeliveryState
} from '@/services/publicProfileDeliveryService';

/**
 * Resolves whether an officer's public page is actually delivering their
 * current content, by comparing the live RPC revision against the revision
 * published in the deployed site manifest.
 *
 * Public manifest/revision checks remain available to employees. Build state
 * and rebuild RPCs are admin-only, so never attempt them for a non-admin or
 * while authentication is unresolved. The database still enforces access.
 */
export function usePublicProfileDelivery(slug: string | null, published: boolean) {
  const { user, session, loading: authLoading } = useAuth();
  const actorId = user?.id;
  const canManageBuilds = !authLoading
    && Boolean(actorId && session?.user.id === actorId)
    && user?.employment_status === 'active'
    && hasProfileAdminRole(user?.roles);
  const [state, setState] = useState<ProfileDeliveryState | null>(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const requestSequence = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!slug) {
      setState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [manifest, expectedRevision, buildState] = await Promise.all([
      fetchPublicProfileManifest(),
      published ? fetchExpectedRevision(slug) : Promise.resolve(null),
      canManageBuilds ? fetchBuildState() : Promise.resolve(null)
    ]);
    if (requestId !== requestSequence.current) {
      return;
    }
    setState(deriveDeliveryState({ published, slug, expectedRevision, manifest, buildState }));
    setLoading(false);
  }, [canManageBuilds, published, slug]);

  useEffect(() => {
    // A new officer or actor must never inherit the previous delivery state.
    setState(null);
    setRequesting(false);
    void refresh();
    return () => {
      requestSequence.current += 1;
    };
  }, [actorId, refresh]);

  const requestBuild = useCallback(async () => {
    if (!canManageBuilds) {
      return { ok: false, forbidden: true } as const;
    }
    const requestId = requestSequence.current;
    setRequesting(true);
    const result = await requestPublicProfileBuild();
    if (requestId === requestSequence.current) {
      setRequesting(false);
      await refresh();
    }
    return result;
  }, [canManageBuilds, refresh]);

  return { state, loading, requesting, refresh, requestBuild };
}
